import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Responsive from "@amcharts/amcharts5/themes/Responsive";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";

import { getPattern } from "./ChartMain";

interface PieChartConfig {
  id: string;
  filepath: string;
  dataString: string;
  noDeci?: boolean;
  hideLegend?: boolean;
}

interface PieChartData {
  name: string;
  value: number;
  tooltiptext: string;
  color: am5.Color;
  pattern: am5.Pattern | undefined;
}
interface ChartWithId extends am5percent.PieChart {
  id: string;
  data: PieChartData[];
}
interface RootWithId extends am5.Root {
  id: string;
}
// These will store the chart instances
const pieCharts: ChartWithId[] = [];
const pieChartRoots: RootWithId[] = [];

// If global piechartarray is defined, build the charts
declare const piechartarray: PieChartConfig[] | undefined;

if (typeof piechartarray !== "undefined") {
  for (const x of piechartarray) {
    am5.net.load(x.filepath).then((result) => {
      const root = am5.Root.new(x.id) as RootWithId;

      let data: PieChartData[] = [];

      if (x.filepath.includes(".csv")) {
        if (result.response) {
          const delimiter = result.response.includes(";") ? ";" : ",";

          const parsed = am5.CSVParser.parse(result.response, {
            delimiter,
            useColumnNames: true,
          });

          // Note: You may want to adapt this parsing for CSVs with tooltips/colors
          data = parsed.map((item: any) => ({
            name: item.name,
            value: parseFloat(item.value),
            tooltiptext: item.tooltiptext || "",
            color: item.color || "#cccccc",
          }));
        }
      } else if (x.dataString) {
        const dataArr = x.dataString.split(",");
        for (let i = 0; i < dataArr.length; i += 5) {
          data.push({
            name: dataArr[i],
            value: parseFloat(dataArr[i + 1]),
            tooltiptext: dataArr[i + 2],
            color: am5.color(dataArr[i + 3]),
            pattern: getPattern(dataArr[i + 3], dataArr[i + 4], root)
          });
        }
      }

      const chart = root.container.children.push(
        am5percent.PieChart.new(root, {
          layout: root.verticalLayout
        })
      ) as ChartWithId;

      // Create series
      // https://www.amcharts.com/docs/v5/charts/percent-charts/pie-chart/#Series
      const series = chart.series.push(
        am5percent.PieSeries.new(root, {
          valueField: "value",
          categoryField: "name",
        })
      );

      const labelFormat = x.noDeci
        ? "{name}:\n{valuePercentTotal.formatNumber('0')}%"
        : "{name}:\n{valuePercentTotal.formatNumber('0.00')}%";

      series.labels.template.setAll({
        text: labelFormat,
        oversizedBehavior: "wrap",
        maxWidth: 140,
      });

      series.slices.template.setAll({
        focusable: true,
        ariaLabel: "{name}. {valuePercentTotal.formatNumber('#.#')}%. {tooltiptext}.",
        role: "figure",
        fillOpacity: 0.5,
        stroke: am5.color(0xffffff),
        strokeWidth: 2,
      });

      series.slices.template.adapters.add("fillPattern", (fill, target) => {
        const ctx = target.dataItem?.dataContext as PieChartData;
        return ctx?.pattern ?? fill;
      });

      const tooltip = am5.Tooltip.new(root, {
        getFillFromSprite: false,
        autoTextColor: false,
        labelText: "{tooltiptext}",
      });

      tooltip.label.set("fill", am5.color(0xffffff));

      // Set the background fill color
      tooltip.get("background")?.setAll({
        fill: am5.color("#696969")
      });

      series.slices.template.setAll({
        fillOpacity: 0.5,
        stroke: am5.color(0xffffff),
        strokeWidth: 2
      });

      let colorarray: any[] = [];
      data.forEach(function (item) {
        colorarray.push(item.color);
      });

      series.get("colors")?.set("colors", colorarray);

      // Set data
      // https://www.amcharts.com/docs/v5/charts/percent-charts/pie-chart/#Setting_data
      series.set("tooltip", tooltip);

      series.data.processor = am5.DataProcessor.new(root, {
        numericFields: ["value"]
      });

      series.data.setAll(data);

      // Create legend
      // https://www.amcharts.com/docs/v5/charts/percent-charts/legend-percent-series/
      if (!x.hideLegend) {
        const legend = chart.children.push(
          am5.Legend.new(root, {
            centerX: am5.percent(50),
            x: am5.percent(50),
            marginTop: 15,
            marginBottom: 15,
          })
        );
        legend.data.setAll(series.dataItems);
      }

      // Responsive
      const responsive = am5themes_Responsive.newEmpty(root);
      responsive.addRule({
        relevant: am5themes_Responsive.widthL,
        settings: {
          inside: true
        },
        applying: () => {
          series.labels.template.set("visible", false);
          series.ticks.template.set("visible", false);
        },
        removing: () => {
          series.labels.template.set("visible", false);
          series.ticks.template.set("visible", false);
        }
      });


      root.setThemes([
        am5themes_Animated.new(root),
        responsive
      ]);

      // Play initial series animation
      // https://www.amcharts.com/docs/v5/concepts/animations/#Animation_of_series
      series.appear(1000);
      chart.appear(1000, 100);

      // Track charts
      chart.id = x.id;
      chart.data = data;
      root.id = x.id;

      pieCharts.push(chart);
      pieChartRoots.push(root);
    });
  }

}
  // Dropdown toggle
  export function pieChartDropDown(element: HTMLElement): void {
    element.nextElementSibling?.classList.toggle("show");
  }

  // Export chart
  export function exportPieChart(format: "png" | "jpg" | "pdf" | "xlsx" | "csv" | "json" | "pdfdata", id: string): void {
    const chart = pieCharts.find(ch => (ch as any).id === id);
    const root = pieChartRoots.find(rt => (rt as any).id === id);
    const data = (chart as any).data;

    if (!chart || !root || !data) return;

    const exporting = am5plugins_exporting.Exporting.new(root, {
      dataSource: data,
      filePrefix: "Cirkeldiagram",
    });

    exporting.download(format);
  }
